import React, { useState, useEffect } from 'react';
import { Table, Card, Button, Space, Tag, Input, Select, message, Modal } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import request from '../utils/request';
import type { ColumnsType } from 'antd/es/table';

// 配置 axios 默认请求头
request.defaults.headers.common['Authorization'] = `Bearer ${localStorage.getItem('admin_token')}`;

interface LoanRecord {
  id: number;
  user_id: number;
  wallet_address: string;
  amount: number;
  status: number;
  created_at: string;
  updated_at: string;
  admin_name: string;
}

const LendingManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [loans, setLoans] = useState<LoanRecord[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchParams, setSearchParams] = useState({
    address: '',
    status: undefined as number | undefined,
  });
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState<LoanRecord | null>(null);
  const [reviewStatus, setReviewStatus] = useState<number>(1);
  const [actualAmount, setActualAmount] = useState<number>(0);

  const fetchLoans = async () => {
    try {
      setLoading(true);
      const response = await request.get('/loan/list', {
        params: {
          page: pagination.current,
          pageSize: pagination.pageSize,
          ...searchParams
        }
      });
      
      if (response.code === 200) {
        setLoans(response.data.list);
        setPagination(prev => ({
          ...prev,
          total: response.data.total
        }));
      } else {
        message.error(response.message || '获取借款列表失败');
      }
    } catch (error) {
      console.error('获取借款列表失败:', error);
      message.error('获取借款列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async () => {
    if (!selectedLoan || !actualAmount) {
      message.error('请输入实际借款金额');
      return;
    }

    try {
      const response = await request.post('/loan/review', {
        id: selectedLoan.id,
        status: reviewStatus,
        adminId: JSON.parse(localStorage.getItem('admin_user') || '{}').id,
        actualAmount: actualAmount
      });

      if (response.code === 200) {
        message.success('审核成功');
        setReviewModalVisible(false);
        setActualAmount(0);
        fetchLoans();
      } else {
        message.error(response.message || '审核失败');
      }
    } catch (error) {
      console.error('审核失败:', error);
      message.error('审核失败');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const response = await request.delete(`/loan/${id}`);
      if (response.code === 200) {
        message.success('删除成功');
        fetchLoans();
      } else {
        message.error(response.message || '删除失败');
      }
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    }
  };

  const columns: ColumnsType<LoanRecord> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '钱包地址',
      dataIndex: 'wallet_address',
      key: 'wallet_address',
      width: 400,
      render: (address) => (
        <div style={{ wordBreak: 'break-all', whiteSpace: 'normal' }}>
          {address || '-'}
        </div>
      ),
    },
    {
      title: '额度',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (amount) => `${amount} USDT`,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const statusMap = {
          0: { color: 'gold', text: '待审核' },
          1: { color: 'green', text: '已通过' },
          2: { color: 'red', text: '已拒绝' },
        };
        const { color, text } = statusMap[status as keyof typeof statusMap] || { color: 'default', text: '未知' };
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (time) => new Date(time).toLocaleString(),
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 180,
      render: (time) => new Date(time).toLocaleString(),
    },
    {
      title: '管理员',
      dataIndex: 'admin_name',
      key: 'admin_name',
      width: 120,
      render: (name) => name || '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Space size="middle">
          {record.status === 0 && (
            <Button
              type="link"
              onClick={() => {
                setSelectedLoan(record);
                setReviewModalVisible(true);
                setActualAmount(0);
              }}
            >
              审核
            </Button>
          )}
          <Button
            type="link"
            danger
            onClick={() => {
              Modal.confirm({
                title: '确认删除',
                content: '确定要删除这条借款记录吗？',
                okText: '确定',
                cancelText: '取消',
                onOk: () => handleDelete(record.id)
              });
            }}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    fetchLoans();
  }, [pagination.current, pagination.pageSize, searchParams]);

  return (
    <div>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Input
              placeholder="输入钱包地址搜索"
              allowClear
              prefix={<SearchOutlined />}
              style={{ width: 300 }}
              value={searchParams.address}
              onChange={(e) => setSearchParams(prev => ({ ...prev, address: e.target.value }))}
            />
            <Select
              placeholder="选择状态"
              style={{ width: 120 }}
              allowClear
              value={searchParams.status}
              onChange={(value) => setSearchParams(prev => ({ ...prev, status: value }))}
            >
              <Select.Option value={0}>待审核</Select.Option>
              <Select.Option value={1}>已通过</Select.Option>
              <Select.Option value={2}>已拒绝</Select.Option>
            </Select>
            <Button type="primary" onClick={() => fetchLoans()}>
              搜索
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={loans}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
          onChange={(newPagination) => {
            if (newPagination.current && newPagination.pageSize) {
              setPagination({
                current: newPagination.current,
                pageSize: newPagination.pageSize,
                total: pagination.total
              });
            }
          }}
        />
      </Card>

      <Modal
        title="审核借款申请"
        open={reviewModalVisible}
        onOk={handleReview}
        onCancel={() => {
          setReviewModalVisible(false);
          setActualAmount(0);
        }}
        okText="确定"
        cancelText="取消"
      >
        {selectedLoan && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <div>钱包地址: {selectedLoan.wallet_address}</div>
              <div>可借额度: {selectedLoan.amount} USDT</div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div>实际借款金额:</div>
              <Input
                type="number"
                value={actualAmount}
                onChange={(e) => setActualAmount(Number(e.target.value))}
                placeholder="请输入实际借款金额"
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <div>审核结果:</div>
              <Space>
                <Button
                  type={reviewStatus === 1 ? 'primary' : 'default'}
                  onClick={() => setReviewStatus(1)}
                >
                  通过
                </Button>
                <Button
                  type={reviewStatus === 2 ? 'primary' : 'default'}
                  onClick={() => setReviewStatus(2)}
                >
                  拒绝
                </Button>
              </Space>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default LendingManagement; 